import { KeySet } from '@tableau/data-structures';
import {
  ButtonType,
  ButtonWidget,
} from '@tableau/widgets';
import * as React from 'react';
import { Messages } from '../../../../gen-src/Localize';
import { ApiRequestsTypes } from '../../apiRequests/ApiRequests';
import { ApiRequestsContext } from '../../apiRequests/ApiRequestsContext';
import { ComposeEmailDialogContainer, DialogResultParams } from './ComposeEmailDialogContainer';
import { ComposeEmailDialogStyles } from './ComposeEmailDialogStyles';

export type ComposeEmailPaneButtonProps = {
  readonly selectedUserIds: KeySet;
  readonly contentType: ApiRequestsTypes.SendImpactAnalysisEmailContentType;
  readonly contentId: string;
};

/**
 * Make sure to follow the [Rules of Hooks](https://reactjs.org/docs/hooks-rules.html) when consuming this component!
 * Hooks have bitten me in the past since the components I wrote were consumed in a loop, fortunately this component is consumed in
 * [DatabaseDownstreamOwnersPane], [FlowDownstreamOwnersPane], [DatasourceDownstreamOwnersPane], and [TableDownstreamOwnersPane]
 * so this implementation very clearly follows the rules of hooks.
 * Also, since it is not exposed in DataCatClient.ts it isn't supported by the external contract of data catalog client components 😄
 */
export function ComposeEmailPaneButton(props: ComposeEmailPaneButtonProps): JSX.Element {
  const apiRequests = React.useContext(ApiRequestsContext);
  const [hasPermission, setHasPermission] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  React.useEffect(() => {
    apiRequests.canUserSendImpactAnalysisEmail({ contentId: props.contentId, contentType: props.contentType })
      .then(r => setHasPermission(r))
      .catch(_ => { return; });
  },
    [props.contentId, props.contentType, apiRequests] // Don't re-run the permissions check every time someone (un)checks a box
  );

  const launchDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  async function sendEmailAndCloseDialog(a: DialogResultParams) {
    try {
      await apiRequests.sendImpactAnalysisEmailAndShowToast({
        contentType: props.contentType,
        contentId: props.contentId,
        subject: a.subjectText,
        body: a.bodyText,
        userIds: Object.keys(props.selectedUserIds)
      });

      // TODO: remove toasting from vizportalclient and do that here
      closeDialog();
    } catch {
      // TODO: remove toasting from vizportalclient and do that here
      closeDialog();
    }
  }

  const hasSelection = Object.keys(props.selectedUserIds).length >= 1;
  const buttonEnabled = hasPermission && hasSelection;
  const tooltipText = !hasPermission
    ? Messages.SendEmailButtonNoPermissions()
    : !hasSelection ? Messages.SendEmailButtonNoSelection() : undefined;

  return (
    <>
      <ButtonWidget
        handleClick={launchDialog}
        buttonType={ButtonType.Outline}
        style={ComposeEmailDialogStyles.emailButtonStyle}
        disabled={!(buttonEnabled)}
        testId={ComposeEmailDialogStyles.TEST_ID('compose-button')}
        title={tooltipText}
      >
        {Messages.SendEmail()}
      </ButtonWidget>
      {
        isDialogOpen && (
          <ComposeEmailDialogContainer
            selectedUserIds={props.selectedUserIds}
            dismissDialog={closeDialog}
            submitResult={sendEmailAndCloseDialog}
          />
        )
      }
    </>
  );
}
